/**
 * 관리자(ADMIN) 도메인 React Query 훅
 *
 * 잔류물질안전정보시스템 v3의 관리자 기능 관련 모든 데이터 페칭/변이 훅을 정의한다.
 * - 대시보드(Dashboard): 요약, 방문자 추이, 데이터 현황, 사용자 통계, API 사용량, 품질 점수, 보안 이벤트
 * - 사용자 관리(Users): CRUD + 시도/권한템플릿 + 비밀번호 변경 URL
 * - 권한 템플릿(Auth Templates): CRUD
 * - 방문자(Visitors): 목록 조회
 * - 감사 로그(Audit): 목록 조회 + 내보내기
 * - 워크플로우(Workflow): 목록/대기/단계 조회 + 승인/반려
 * - 알림(Notifications): 목록/읽음/미읽음수/설정
 * - 배치 작업(Batch): 목록/생성/항목/재시도
 * - 품질 관리(Quality): 규칙/결과/검사 실행
 * - 팝업 관리(Popups): CRUD
 */
import { adminApi } from '../../api/v2Api';
import {
  QUERY_KEYS,
  STALE_TIMES,
  useV2Query,
  useV2Mutation,
  useV2PaginatedQuery,
  extractData,
} from './useV2Query';

// ═══════════════════════════════════════════════════════════
// 대시보드(Dashboard)
// ═══════════════════════════════════════════════════════════

/** 대시보드 요약 조회 */
export const useDashboardSummary = (options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'dashboard', 'summary'],
    () => adminApi.getSummary(),
    {
      staleTime: STALE_TIMES.SHORT,
      select: extractData,
      ...options,
    }
  );
};

/** 대시보드 방문자 추이 조회 */
export const useDashboardVisitorTrends = (params, options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'dashboard', 'visitor-trends', JSON.stringify(params)],
    () => adminApi.getVisitorTrends(params),
    {
      staleTime: STALE_TIMES.MEDIUM,
      select: extractData,
      deps: [JSON.stringify(params)],
      ...options,
    }
  );
};

/** 대시보드 데이터 현황 조회 */
export const useDashboardDataStatus = (options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'dashboard', 'data-status'],
    () => adminApi.getDataStatus(),
    {
      staleTime: STALE_TIMES.MEDIUM,
      select: extractData,
      ...options,
    }
  );
};

/** 대시보드 사용자 통계 조회 */
export const useDashboardUserStats = (options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'dashboard', 'user-stats'],
    () => adminApi.getUserStats(),
    {
      staleTime: STALE_TIMES.MEDIUM,
      select: extractData,
      ...options,
    }
  );
};

/** 대시보드 API 사용량 조회 */
export const useDashboardApiUsage = (params, options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'dashboard', 'api-usage', JSON.stringify(params)],
    () => adminApi.getApiUsage(params),
    {
      staleTime: STALE_TIMES.MEDIUM,
      select: extractData,
      deps: [JSON.stringify(params)],
      ...options,
    }
  );
};

/** 대시보드 품질 점수 조회 */
export const useDashboardQualityScore = (options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'dashboard', 'quality-score'],
    () => adminApi.getDashboardQualityScore(),
    {
      staleTime: STALE_TIMES.MEDIUM,
      select: extractData,
      ...options,
    }
  );
};

/** 대시보드 보안 이벤트 조회 */
export const useDashboardSecurityEvents = (params, options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'dashboard', 'security-events', JSON.stringify(params)],
    () => adminApi.getDashboardSecurityEvents(params),
    {
      staleTime: STALE_TIMES.SHORT,
      select: extractData,
      deps: [JSON.stringify(params)],
      ...options,
    }
  );
};

// ═══════════════════════════════════════════════════════════
// 사용자 관리(Users)
// ═══════════════════════════════════════════════════════════

/**
 * 관리자 사용자 목록 조회 — 전체 한번에 조회 (클라이언트 사이드 페이지네이션)
 *
 * 사용자 수가 많지 않아 전체를 한번에 받아오고 Ant Design Table이 클라이언트 사이드로
 * 페이지네이션/정렬/필터링을 처리한다. 키워드 검색도 클라이언트 사이드에서 수행한다.
 */
export const useAdminUserList = (options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'users', 'list'],
    () => adminApi.getUserList(),
    {
      staleTime: STALE_TIMES.STANDARD,
      select: extractData,
      ...options,
    }
  );
};

/** 관리자 사용자 상세 조회 */
export const useAdminUserDetail = (userId, options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'users', 'detail', userId],
    () => adminApi.getUserDetail(userId),
    {
      enabled: !!userId,
      select: extractData,
      ...options,
    }
  );
};

/** 사용자 등록 */
export const useCreateUser = (options = {}) => {
  return useV2Mutation((data) => adminApi.createUser(data), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '사용자가 등록되었습니다.',
    ...options,
  });
};

/** 사용자 수정 */
export const useUpdateUser = (options = {}) => {
  return useV2Mutation(({ id, data }) => adminApi.updateUser(id, data), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '사용자 정보가 수정되었습니다.',
    ...options,
  });
};

/** 사용자 삭제 */
export const useDeleteUser = (options = {}) => {
  return useV2Mutation((id) => adminApi.removeUser(id), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '사용자가 삭제되었습니다.',
    ...options,
  });
};

/** 관리자 시도 목록 조회 */
export const useAdminProvinces = (options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'users', 'provinces'],
    () => adminApi.getProvinces(),
    {
      staleTime: STALE_TIMES.STATIC,
      select: extractData,
      ...options,
    }
  );
};

/** 관리자 권한 템플릿 목록 조회 (사용자 관리용) */
export const useAdminAuthTemplates = (options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'users', 'auth-templates'],
    () => adminApi.getAuthTemplates(),
    {
      staleTime: STALE_TIMES.STANDARD,
      select: extractData,
      ...options,
    }
  );
};

/** 비밀번호 변경 URL 생성 */
export const useCreatePasswordChangeUrl = (options = {}) => {
  return useV2Mutation((userId) => adminApi.createPasswordChangeUrl(userId), {
    successMessage: '비밀번호 변경 URL이 생성되었습니다.',
    ...options,
  });
};

/** 사용자 계정 잠금 해제 */
export const useUnlockUser = (options = {}) => {
  return useV2Mutation((userId) => adminApi.unlockUser(userId), {
    invalidateKeys: [QUERY_KEYS.ADMIN, 'users'],
    successMessage: '계정 잠금이 해제되었습니다.',
    ...options,
  });
};

// ═══════════════════════════════════════════════════════════
// 권한 템플릿(Auth Templates)
// ═══════════════════════════════════════════════════════════

/**
 * 권한 템플릿 목록 조회 — 전체 한번에 조회 (클라이언트 사이드 페이지네이션)
 *
 * 템플릿 수가 많지 않아 전체를 한번에 받아오고 Ant Design Table이 클라이언트 사이드로
 * 페이지네이션/정렬/필터링을 처리한다. 키워드 검색도 클라이언트 사이드에서 수행한다.
 */
export const useAuthTemplateList = (options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'auth-templates', 'list'],
    () => adminApi.getAuthTemplateList(),
    {
      staleTime: STALE_TIMES.STANDARD,
      select: extractData,
      ...options,
    }
  );
};

/** 권한 템플릿 상세 조회 */
export const useAuthTemplateDetail = (id, options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'auth-templates', 'detail', id],
    () => adminApi.getAuthTemplateDetail(id),
    {
      enabled: !!id,
      select: extractData,
      ...options,
    }
  );
};

/** 권한 템플릿 등록 */
export const useCreateAuthTemplate = (options = {}) => {
  return useV2Mutation((data) => adminApi.createAuthTemplate(data), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '권한 템플릿이 등록되었습니다.',
    ...options,
  });
};

/** 권한 템플릿 수정 */
export const useUpdateAuthTemplate = (options = {}) => {
  return useV2Mutation(({ id, data }) => adminApi.updateAuthTemplate(id, data), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '권한 템플릿이 수정되었습니다.',
    ...options,
  });
};

/** 권한 템플릿 삭제 */
export const useDeleteAuthTemplate = (options = {}) => {
  return useV2Mutation((id) => adminApi.removeAuthTemplate(id), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '권한 템플릿이 삭제되었습니다.',
    ...options,
  });
};

// ═══════════════════════════════════════════════════════════
// 방문자(Visitors)
// ═══════════════════════════════════════════════════════════

/** 방문자 목록 조회 */
export const useVisitorList = (params, options = {}) => {
  return useV2PaginatedQuery(
    [QUERY_KEYS.ADMIN, 'visitors', 'list', JSON.stringify(params)],
    () => adminApi.getVisitorList(params),
    {
      staleTime: STALE_TIMES.STANDARD,
      deps: [JSON.stringify(params)],
      ...options,
    }
  );
};

/** 방문자 통계 요약 조회 */
export const useVisitorSummary = (options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'visitors', 'summary'],
    () => adminApi.getVisitorSummary(),
    {
      staleTime: STALE_TIMES.SHORT,
      select: extractData,
      ...options,
    }
  );
};

/** 방문자 일별 추이 조회 */
export const useVisitorDailyTrends = (params, options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'visitors', 'trends', JSON.stringify(params)],
    () => adminApi.getVisitorTrendsByDate(params),
    {
      staleTime: STALE_TIMES.MEDIUM,
      select: extractData,
      deps: [JSON.stringify(params)],
      ...options,
    }
  );
};

// ═══════════════════════════════════════════════════════════
// 감사 로그(Audit)
// ═══════════════════════════════════════════════════════════

/** 감사 로그 목록 조회 */
export const useAuditLogs = (params, options = {}) => {
  return useV2PaginatedQuery(
    [QUERY_KEYS.ADMIN, 'audit', 'list', JSON.stringify(params)],
    () => adminApi.getAuditLogs(params),
    {
      staleTime: STALE_TIMES.STANDARD,
      deps: [JSON.stringify(params)],
      ...options,
    }
  );
};

/** 감사 로그 내보내기 */
export const useExportAuditLogs = (options = {}) => {
  return useV2Mutation((params) => adminApi.exportAuditLogs(params), {
    successMessage: '감사 로그 내보내기가 완료되었습니다.',
    ...options,
  });
};

// ═══════════════════════════════════════════════════════════
// 워크플로우(Workflow)
// ═══════════════════════════════════════════════════════════

/** 워크플로우 목록 조회 */
export const useWorkflowList = (params, options = {}) => {
  return useV2PaginatedQuery(
    [QUERY_KEYS.ADMIN, 'workflows', 'list', JSON.stringify(params)],
    () => adminApi.getWorkflows(params),
    {
      staleTime: STALE_TIMES.STANDARD,
      deps: [JSON.stringify(params)],
      ...options,
    }
  );
};

/** 대기 중 워크플로우 조회 */
export const usePendingWorkflows = (params, options = {}) => {
  return useV2PaginatedQuery(
    [QUERY_KEYS.ADMIN, 'workflows', 'pending', JSON.stringify(params)],
    () => adminApi.getPendingWorkflows(params),
    {
      staleTime: STALE_TIMES.SHORT,
      deps: [JSON.stringify(params)],
      ...options,
    }
  );
};

/** 워크플로우 단계 조회 */
export const useWorkflowSteps = (id, options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'workflows', 'steps', id],
    () => adminApi.getWorkflowSteps(id),
    {
      enabled: !!id,
      select: extractData,
      ...options,
    }
  );
};

/** 워크플로우 승인 */
export const useApproveWorkflow = (options = {}) => {
  return useV2Mutation(({ id, data }) => adminApi.approveWorkflow(id, data), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '워크플로우가 승인되었습니다.',
    ...options,
  });
};

/** 워크플로우 반려 */
export const useRejectWorkflow = (options = {}) => {
  return useV2Mutation(({ id, data }) => adminApi.rejectWorkflow(id, data), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '워크플로우가 반려되었습니다.',
    ...options,
  });
};

// ═══════════════════════════════════════════════════════════
// 알림(Notifications)
// ═══════════════════════════════════════════════════════════

/** 알림 목록 조회 */
export const useNotificationList = (params, options = {}) => {
  return useV2PaginatedQuery(
    [QUERY_KEYS.ADMIN, 'notifications', 'list', JSON.stringify(params)],
    () => adminApi.getNotifications(params),
    {
      staleTime: STALE_TIMES.SHORT,
      deps: [JSON.stringify(params)],
      ...options,
    }
  );
};

/** 알림 읽음 처리 */
export const useMarkNotificationRead = (options = {}) => {
  return useV2Mutation((id) => adminApi.markNotificationRead(id), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    ...options,
  });
};

/** 읽지 않은 알림 수 조회 */
export const useUnreadNotificationCount = (options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'notifications', 'unread-count'],
    () => adminApi.getUnreadNotificationCount(),
    {
      staleTime: STALE_TIMES.SHORT,
      select: extractData,
      ...options,
    }
  );
};

/** 알림 설정 조회 */
export const useNotificationSettings = (options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'notifications', 'settings'],
    () => adminApi.getNotificationSettings(),
    {
      staleTime: STALE_TIMES.STANDARD,
      select: extractData,
      ...options,
    }
  );
};

/** 알림 설정 수정 */
export const useUpdateNotificationSettings = (options = {}) => {
  return useV2Mutation((data) => adminApi.updateNotificationSettings(data), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '알림 설정이 수정되었습니다.',
    ...options,
  });
};

// ═══════════════════════════════════════════════════════════
// 배치 작업(Batch)
// ═══════════════════════════════════════════════════════════

/** 배치 작업 목록 조회 */
export const useBatchJobList = (params, options = {}) => {
  return useV2PaginatedQuery(
    [QUERY_KEYS.ADMIN, 'batch', 'list', JSON.stringify(params)],
    () => adminApi.getBatchJobs(params),
    {
      staleTime: STALE_TIMES.STANDARD,
      deps: [JSON.stringify(params)],
      ...options,
    }
  );
};

/** 배치 작업 생성 */
export const useCreateBatchJob = (options = {}) => {
  return useV2Mutation((data) => adminApi.createBatchJob(data), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '배치 작업이 생성되었습니다.',
    ...options,
  });
};

/** 배치 항목 조회 */
export const useBatchItems = (id, params, options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'batch', 'items', id, JSON.stringify(params)],
    () => adminApi.getBatchItems(id, params),
    {
      enabled: !!id,
      select: extractData,
      deps: [JSON.stringify(params)],
      ...options,
    }
  );
};

/** 배치 작업 재시도 */
export const useRetryBatchJob = (options = {}) => {
  return useV2Mutation((id) => adminApi.retryBatchJob(id), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '배치 작업이 재시도되었습니다.',
    ...options,
  });
};

// ═══════════════════════════════════════════════════════════
// 품질 관리(Quality)
// ═══════════════════════════════════════════════════════════

/** 품질 규칙 목록 조회 */
export const useQualityRules = (params, options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'quality', 'rules', JSON.stringify(params)],
    () => adminApi.getQualityRules(params),
    {
      staleTime: STALE_TIMES.STANDARD,
      select: extractData,
      deps: [JSON.stringify(params)],
      ...options,
    }
  );
};

/** 품질 검사 결과 조회 */
export const useQualityResults = (params, options = {}) => {
  return useV2PaginatedQuery(
    [QUERY_KEYS.ADMIN, 'quality', 'results', JSON.stringify(params)],
    () => adminApi.getQualityResults(params),
    {
      staleTime: STALE_TIMES.STANDARD,
      deps: [JSON.stringify(params)],
      ...options,
    }
  );
};

/** 품질 검사 실행 */
export const useRunQualityCheck = (options = {}) => {
  return useV2Mutation((data) => adminApi.runQualityCheck(data), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '품질 검사가 실행되었습니다.',
    ...options,
  });
};

// ═══════════════════════════════════════════════════════════
// 팝업 관리(Popups)
// ═══════════════════════════════════════════════════════════

/** 팝업 목록 조회 */
export const usePopupList = (params, options = {}) => {
  return useV2PaginatedQuery(
    [QUERY_KEYS.ADMIN, 'popups', 'list', JSON.stringify(params)],
    () => adminApi.getPopupList(params),
    {
      staleTime: STALE_TIMES.STANDARD,
      deps: [JSON.stringify(params)],
      ...options,
    }
  );
};

/** 팝업 상세 조회 */
export const usePopupDetail = (id, options = {}) => {
  return useV2Query(
    [QUERY_KEYS.ADMIN, 'popups', 'detail', id],
    () => adminApi.getPopupDetail(id),
    {
      enabled: !!id,
      select: extractData,
      ...options,
    }
  );
};

/** 팝업 등록 */
export const useCreatePopup = (options = {}) => {
  return useV2Mutation((data) => adminApi.createPopup(data), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '팝업이 등록되었습니다.',
    ...options,
  });
};

/** 팝업 수정 */
export const useUpdatePopup = (options = {}) => {
  return useV2Mutation(({ id, data }) => adminApi.updatePopup(id, data), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '팝업이 수정되었습니다.',
    ...options,
  });
};

/** 팝업 삭제 */
export const useDeletePopup = (options = {}) => {
  return useV2Mutation((id) => adminApi.deletePopup(id), {
    invalidateKeys: QUERY_KEYS.ADMIN,
    successMessage: '팝업이 삭제되었습니다.',
    ...options,
  });
};
